import styled from "styled-components";

import { colorsPalettes, rgba, setFont } from "@similarweb/styles";
import { Box, Tab } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { Spinner } from "pro-features/components/Loaders/src/Spinner";

export const NoDataTitle = styled.div`
    ${setFont({ $size: "16px", $color: colorsPalettes.carbon[500] })};
    margin-top: 10px;
`;

export const NoDataSubTitle = styled.div`
    ${setFont({ $size: "12px", $color: rgba(colorsPalettes.carbon[500], 0.6) })};
    text-align: center;
`;

export interface INoDataContainerProps {
    paddingBottom?: string;
    paddingTop?: string;
}
export const NoDataContainer = styled.div<INoDataContainerProps>`
    ${({ paddingTop = "0" }) => `padding-top: ${paddingTop}`};
    ${({ paddingBottom = "0" }) => `padding-bottom: ${paddingBottom}`};
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export const SubTitleContainer = styled.div`
    display: flex;
    padding-bottom: 8px;
`;

export const Text = styled.label<{
    margin?: string;
    fontSize?: number;
    fontWeight?: number;
    color?: string;
    opacity?: number;
}>`
    display: inline-block;
    margin: ${({ margin }) => (margin ? margin : "auto 8px auto 0")};
    white-space: nowrap;
    cursor: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    ${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight}`};
    ${({ fontSize = 14, opacity = 0.8, color = colorsPalettes.carbon[500] }) =>
        setFont({ $size: fontSize, $color: rgba(color, opacity) })};
`;

export const TableHeaderText = styled(Text)`
    margin: 0;
    align-self: flex-end;
    padding-bottom: 4px;
`;

export const MetricTitleText = styled(Text)`
    padding-bottom: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${({ fontSize = 20 }) => setFont({ $size: fontSize, $color: colorsPalettes.carbon[500], $weight: 500 })};
`;

const MetricsInnerRowHeader = styled(Text)`
    margin: 16px 16px 0px 16px;
    font-weight: 500;
    line-height: 30px;
    ${setFont({ $size: 20, $color: rgba(colorsPalettes.carbon[500], 1) })};
`;

const MetricContainerWrapper = styled(Box)<{
    width?: string;
    height?: string;
    minHeight?: string;
    padding?: string;
    overflow?: string;
}>`
    ${({ width = "33.3%" }) => `width:${width}`};
    ${({ height = "189px" }) => `height:${height}`};
    ${({ minHeight }) => minHeight && `min-height: ${minHeight};`};
    ${({ padding = "24px" }) => `padding:${padding}`};
    background-color: white;
    @media (max-width: 1400px) {
        ${({ padding = "16px" }) => `padding:${padding}`};
    }
    ${({ overflow = "hidden" }) => `overflow:${overflow}`};
`;

export const MetricContainer = ({ children, ...rest }) => {
    return (
        <MetricContainerWrapper {...rest}>
            <div style={{ position: "relative", height: "100%" }}>{children}</div>
        </MetricContainerWrapper>
    );
};

MetricContainer.HEIGHTS = {
    SINGLE_KEYWORD: "77px",
    KEYWORD_GROUP: "126px",
};

export const MetricsSpace = styled.div`
    margin: 0 12px 0 12px;
    @media (max-width: 1500px) {
        margin: 0 8.5px 0 8.5px;
    }
`;

export const FlexWithSpace = styled.div<{
    width?: string;
    height?: string;
    paddingBottom?: string;
    paddingTop?: string;
}>`
    display: flex;
    justify-content: space-between;
    ${({ width }) => width && `width: ${width}`};
    ${({ height }) => height && `height:${height}`};
    ${({ paddingBottom }) => paddingBottom && `padding-bottom:${paddingBottom}`};
    ${({ paddingTop }) => paddingTop && `padding-top:${paddingTop}`};
`;

export const MetricsRow = styled(FlexWithSpace)<{ marginTop?: string; gap?: string }>`
    ${({ marginTop = "0" }) => `margin-top: ${marginTop}`};
    ${({ gap = "0" }) => `gap: ${gap}`};
`;

export const LoadingSpinner = styled(Spinner)<{ top?: string }>`
    margin: 0 auto;
    position: relative;
    top: ${({ top }) => (top ? top : "40px")};
    width: 55px;
    height: 55px;
`;

export const SeeMoreContainer = styled.div<{ textAlign?: string }>`
    ${({ textAlign = "center" }) => `text-align:${textAlign}`};
    margin-bottom: 16px;
`;

export const GridContainer = styled(FlexWithSpace)<{
    isFirst: boolean;
    isCenter?: boolean;
}>`
    @media print {
        width: 100%;
    }
    @media (max-width: 1350px) {
        width: 100%;
    }
    ${({ isFirst }) => isFirst && `border-top: none`};
    border-top: 1px solid ${colorsPalettes.carbon[100]};
    align-items: center;
    padding: 0 20px 0 20px;
    box-sizing: border-box;
    .Loader--line {
        width: 75%;
        height: 10px;
        margin-bottom: 0px;
        margin-left: 20px;
    }
    ${({ isCenter }) => isCenter && `justify-content: center;`};
`;

export const LoaderContainer = styled.div`
    padding-top: 10px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 90%;
`;

export const ColLoader: any = styled.div<{ width?: string }>`
    margin-right: ${({ isLast }: any) => (isLast ? "0px" : "30px")};
    ${({ width }) => `width: ${width}`};
    padding-bottom: 10px;
    padding-top: 7px;
    box-sizing: border-box;
`;

export const CoreWebsiteCellContainer = styled(Flex)`
    width: 70%;
    @media (max-width: 1600px) {
        width: 50%;
    }
    @media (max-width: 1400px) {
        width: 45%;
    }
`;

const StyledTab = styled(Tab)`
    min-width: 200px;
`;
